import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {api} from "../api";

type Props = {service: string; scopes: string[]};

export default function CachePanel({service, scopes}: Props) {
  const {t} = useTranslation();
  const scopeLabel = (scope: string) => t(service === "dictionary" ? `dictionary.resources.${scope}` : `cache.scopes.${scope}`, {defaultValue: scope});
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const reset = async (scope: string) => {
    if (!window.confirm(t("cache.confirm", {scope: scopeLabel(scope)}))) return;
    setBusy(scope); setMessage(""); setError("");
    try {
      const response = await api.post(`/data/${service}/cache`, {scope});
      const deleted = Number(response.data?.deleted || 0);
      // Some existing language files used `{count}` while i18next expects
      // `{{count}}`. Normalize both forms so the user never sees a literal
      // placeholder in the success message.
      setMessage(t("cache.success", {count: deleted}).replace(/\{\{?count\}?\}/g, String(deleted)));
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || t("cache.failed"));
    } finally { setBusy(""); }
  };
  const scopeButtons = scopes.filter(scope => scope !== "all");
  const hasResetAll = scopes.includes("all");
  const cacheButton = (scope: string, isAll: boolean) => <button key={scope} type="button" className={`cache-action${isAll ? " cache-action--all" : ""}`} title={t("cache.resetTitle", {scope: scopeLabel(scope)})} disabled={Boolean(busy)} onClick={() => reset(scope)}>
    <span className={`cache-action-icon${busy === scope ? " spinning" : ""}`} aria-hidden="true">{isAll ? "⌫" : "↻"}</span>
    <span className="cache-action-label">{busy === scope ? t("cache.resetting") : scopeLabel(scope)}</span>
  </button>;
  return <section className="cache-panel" aria-labelledby="cache-title">
    <div className="panel-heading"><div><span className="eyebrow">{t("cache.eyebrow")}</span><h2 id="cache-title">{t("cache.title")}</h2></div></div>
    <p className="muted">{t("cache.description")}</p>
    {message && <div className="alert success" role="status">{message}</div>}
    {error && <div className="alert" role="alert">{error}</div>}
    <div className="cache-actions">{scopeButtons.map(scope => cacheButton(scope, false))}</div>
    {hasResetAll && <div className="cache-actions-danger">
      <div className="cache-actions-danger-divider"><span>{t("cache.dangerZone", {defaultValue: "Danger zone"})}</span></div>
      {cacheButton("all", true)}
    </div>}
  </section>;
}
